import { CriteoIntegration } from "./_criteoApi";
import {
  showcaseTemplate,
  showcaseProductCardTemplate,
} from "./_showcaseTemplate";

export class CriteoShowcase extends CriteoIntegration {
  #debug = false; // Set to true to enable debug logs
  #selectors = {
    mainPage: "#page .main-content",
    ulList: ".snize-search-results-content.clearfix",
    results: "snize_results",
    showcaseContainer: ".criteo-showcase-container",
    brandImage: ".showcase-brand-image",
    brandTitle: ".showcase-brand-title",
    brandDescription: ".showcase-brand-description",
    brandCta: ".showcase-brand-cta",
    productsZone: ".criteo-showcase-products",
    productCard: ".showcase-product-card",
    productLink: ".showcase-product-link",
    productImage: ".showcase-product-image",
    productTitle: ".showcase-product-title",
    productPrice: ".showcase-product-price",
    productRating: ".showcase-product-rating",
    energyLabel: ".energy-label-badge",
    sliderPrev: ".showcase-slider-prev",
    sliderNext: ".showcase-slider-next",
  };
  #showcaseElement = null;
  #response = null;
  #hasRendered = false;

  static isEnabled = true;

  /**
   * Constructor to initialize debug mode
   * @param {boolean} debug - Enable/disable debug logs
   */
  constructor(debug = false) {
    super();
    this.#debug = debug;
  }

  /**
   * Set the API response from an external source (to avoid duplicate API calls)
   * @param {object} response - The Criteo API response
   */
  setResponse(response) {
    this.#response = response;
  }

  /**
   * Centralized logging method
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  #log(message, data = null) {
    if (!this.#debug) return;

    if (data !== null) {
      console.log(`[Criteo Showcase] ${message}`, data);
    } else {
      console.log(`[Criteo Showcase] ${message}`);
    }
  }

  /**
   * @returns {Promise<HTMLUListElement>}
   * @description Wait for Searchanise to render the grid of products
   */
  async #grid() {
    return new Promise((resolve, _reject) => {
      let grid = document.querySelector(this.#selectors.ulList);

      if (!grid) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach(({ type, addedNodes }) => {
            if (type === "childList") {
              addedNodes.forEach((node) => {
                if (node.id === this.#selectors.results) {
                  observer.disconnect();
                  resolve(node.querySelector(this.#selectors.ulList));
                }
              });
            }
          });
        });

        observer.observe(document.querySelector(this.#selectors.mainPage), {
          childList: true,
          subtree: true,
        });
      } else resolve(grid);
    });
  }

  /**
   * Generates API params for Showcase request
   * @param {'search-results-page' | 'collections'} template
   */
  async #generateParams(template) {
    const grid = await this.#grid();
    const skus = Array.from(grid.querySelectorAll("span.snize-sku")).map(
      (span) => span.innerText
    );

    const [prefix, id] = window.criteoCategory?.split("_") || [];

    const params = {
      item: skus.join("|"),
      "list-size": skus.length,
      "page-number": 1,
      ...(template === "collections" && { category: `${prefix}_${id}` }),
      ...(template === "search-results-page" && {
        keywords: (() => {
          const urlParams = new URLSearchParams(window.location.search);

          if (urlParams.has("rb_product_type")) {
            return urlParams.get("rb_product_type").toLowerCase();
          }

          return decodeURI(
            window.location.search
              .replace("?q=", "")
              .split("&")[0]
              .replace(/%20/g, " ")
          );
        })(),
      }),
    };

    return params;
  }

  /**
   * Creates a product card for the Showcase
   * @param {object} product - Shopify product with Criteo data
   */
  #createProductCard(product) {
    const cardContainer = document.createElement("div");
    cardContainer.innerHTML = showcaseProductCardTemplate;
    const card = cardContainer.firstElementChild;

    const price = parseFloat(product.priceRange.minVariantPrice.amount);
    const compareAt = parseFloat(
      product.compareAtPriceRange.minVariantPrice.amount
    );

    // Set product image
    card.querySelector(this.#selectors.productImage).src =
      product.featuredImage?.src;
    card.querySelector(this.#selectors.productImage).alt = product.title;

    // Set product title
    card.querySelector(this.#selectors.productTitle).innerText = product.title;

    // Set product link
    const productLink = card.querySelector(this.#selectors.productLink);
    productLink.href = product.onlineStoreUrl;
    productLink.title = product.title;

    // Set product price
    card.querySelector(this.#selectors.productPrice).innerHTML =
      this.#priceMarkup(price, compareAt);

    // Set Criteo beacons
    // onView beacon on the card (for viewport tracking)
    card.dataset.criteoViewBeacon = product.criteo?.OnViewBeacon;
    // onClick beacon on the productLink (the clickable element)
    productLink.dataset.criteoOnClickBeacon = product.criteo?.OnClickBeacon;

    // Handle product rating - remove if no rating available
    const ratingContainer = card.querySelector(this.#selectors.productRating);
    if (!product.rating || product.rating === 0) {
      ratingContainer.remove();
      this.#log("No rating found, container removed");
    } else {
      // TODO: Add rating HTML generation when needed
      ratingContainer.remove();
      this.#log("Rating available but not implemented yet");
    }

    // Add energy label if needed
    this.#log("Product tags:", product.tags);
    const energyLabelHTML = this.#getEnergyLabel(product.tags);
    this.#log("Energy label HTML:", energyLabelHTML);
    const energyLabelContainer = card.querySelector(".product-energy-tag");

    if (energyLabelHTML) {
      energyLabelContainer.innerHTML = energyLabelHTML;
      this.#log("Energy label inserted successfully");
    } else {
      energyLabelContainer.remove();
      this.#log("No energy label found, container removed");
    }

    return card;
  }

  /**
   * Gets energy label HTML based on product tags
   * @param {string[]} tags - Product tags
   * @returns {string|null} - HTML of the energy label or null
   */
  #getEnergyLabel(tags) {
    const energies = ["a", "b", "c", "d", "e", "f", "g"];

    // Look for energy-{letter} tag
    for (const tag of tags) {
      const lowerTag = tag.toLowerCase();
      if (lowerTag.startsWith("energy-")) {
        const energyLetter = lowerTag.split("-")[1];
        if (energies.includes(energyLetter)) {
          return `
            <div class="energy-tag-wrapper energy-${energyLetter}">
              <div class="energy-scale">
                <span class="letter">A</span>
                <span class="arrow">
                  <svg viewBox="0 0 23 32">
                    <path d="M10.397 32a.8.8 0 01-.8-.8V5.328l-7.104 7.12a.8.8 0 01-1.136 0l-1.12-1.136a.8.8 0 010-1.136L10.045.352c.224-.225.53-.352.848-.352h.608c.317.004.62.13.848.352l9.808 9.824a.8.8 0 010 1.136l-1.136 1.136a.784.784 0 01-1.12 0l-7.104-7.12V31.2a.8.8 0 01-.8.8h-1.6z" fill-rule="evenodd"></path>
                  </svg>
                </span>
                <span class="letter">G</span>
              </div>
              <div class="energy-tag">${energyLetter}</div>
              <div class="energy-shape"></div>
            </div>
          `;
        }
      }
    }

    return null;
  }

  /**
   * Generates price markup
   * @param {number} price
   * @param {number} compareAt
   */
  #priceMarkup(price, compareAt) {
    const priceFormatted = this._formatPrice(price);
    const compareFormatted = this._formatPrice(compareAt);

    return `
      ${
        compareAt > price
          ? `<span class="showcase-compare-price">${compareFormatted}</span>`
          : ""
      }
      <span class="showcase-regular-price ${
        compareAt > price ? `showcase-sale-price` : ""
      }">
        ${priceFormatted}
      </span>
      <span class="price-tax">IGIC Incl.</span>
    `;
  }

  /**
   * Sets up the slider arrows click and scroll behavior
   * @param {HTMLElement} showcase - The showcase container
   * @param {HTMLElement} productsZone - The scrollable products container
   */
  #setupSliderArrow(showcase, productsZone) {
    const prevArrow = showcase.querySelector(this.#selectors.sliderPrev);
    const nextArrow = showcase.querySelector(this.#selectors.sliderNext);

    if (!prevArrow || !nextArrow) return;

    const updateArrows = () => {
      const isAtStart = productsZone.scrollLeft <= 5;
      const isAtEnd =
        productsZone.scrollLeft + productsZone.clientWidth >=
        productsZone.scrollWidth - 5;
      prevArrow.classList.toggle("is-hidden", isAtStart);
      nextArrow.classList.toggle("is-hidden", isAtEnd);
    };

    const getScrollAmount = () => {
      const card = productsZone.querySelector(this.#selectors.productCard);
      return card ? card.offsetWidth + 8 : 200;
    };

    nextArrow.addEventListener("click", () => {
      productsZone.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
    });

    prevArrow.addEventListener("click", () => {
      productsZone.scrollBy({ left: -getScrollAmount(), behavior: "smooth" });
    });

    productsZone.addEventListener("scroll", updateArrows, { passive: true });

    // Initial check after DOM render
    requestAnimationFrame(() => updateArrows());
  }

  /**
   * Renders the Showcase component
   * @param {object} showcaseData - Criteo Showcase placement data
   */
  async #renderShowcase(showcaseData) {
    const {
      creative,
      rendering,
      products,
      OnViewBeacon,
      OnLoadBeacon,
      OnClickBeacon,
    } = showcaseData;

    this.#log("Rendering Showcase with data format:", {
      hasCreative: !!creative,
      hasRendering: !!rendering,
      productsCount: products?.length
    });

    // Create showcase container
    const showcaseContainer = document.createElement("li");
    showcaseContainer.id = "criteo-showcase-wrapper";
    showcaseContainer.innerHTML = showcaseTemplate;

    const showcase = showcaseContainer.querySelector(
      this.#selectors.showcaseContainer
    );

    // Set brand/creative content
    // Handle both 'creative' format (Showcase) and 'rendering' format (topBranding)
    const hasCreative = creative;
    const hasRendering = rendering;

    if (hasCreative || hasRendering) {
      const brandImage = showcase.querySelector(this.#selectors.brandImage);
      const brandTitle = showcase.querySelector(this.#selectors.brandTitle);
      const brandDescription = showcase.querySelector(
        this.#selectors.brandDescription
      );
      const brandCta = showcase.querySelector(this.#selectors.brandCta);

      if (hasCreative) {
        // Original Showcase format
        brandImage.src = creative.image || "";
        brandImage.alt = creative.title || "Promoción";
        brandTitle.innerText = creative.title || "";
        brandDescription.innerText = creative.description || "";

        if (creative.clickUrl) {
          brandCta.href = creative.clickUrl;
          brandCta.dataset.criteoOnClickBeacon = OnClickBeacon;
        } else {
          // No click URL, make it non-clickable
          brandCta.href = "#";
          brandCta.style.pointerEvents = "none";
          brandCta.style.cursor = "default";
        }
      } else if (hasRendering) {
        // topBranding format using 'rendering' field
        // Add special class to handle different layout
        showcaseContainer.classList.add('criteo-topbranding-format');

        const isMobile = screen.width <= 968;
        const backgroundImage = isMobile
          ? rendering.mobile_background_image
          : rendering.desktop_background_image;
        const imageAlt = isMobile
          ? rendering.mobile_background_image_alt_text
          : rendering.desktop_background_image_alt_text;

        brandImage.src = backgroundImage ? `https:${backgroundImage}` : "";
        brandImage.alt = imageAlt || "Promoción";

        // topBranding doesn't have title/description, hide them
        brandTitle.style.display = 'none';
        brandDescription.style.display = 'none';

        // Set CTA from rendering footer
        if (rendering.optional_footer_redirect_url) {
          brandCta.href = rendering.optional_footer_redirect_url;
          brandCta.dataset.criteoOnClickBeacon = OnClickBeacon;
        } else {
          // No click URL, make it non-clickable
          brandCta.href = "#";
          brandCta.style.pointerEvents = "none";
          brandCta.style.cursor = "default";
        }
      }
    }

    // Get product SKUs and fetch from Shopify
    // Get unique SKUs for Shopify query (to avoid duplicate API calls)
    const criteoProducts = products.slice(0, 3);
    const uniqueSKUs = [...new Set(criteoProducts.map((p) => p.ProductId))];

    // Fetch unique products from Shopify
    const shopifyProductsMap = (await this._getProducts(uniqueSKUs)).reduce(
      (map, product) => {
        const sku = product.tags.find((t) => t.startsWith("sku-"))?.replace("sku-", "")
          || product.variants?.nodes?.[0]?.sku;
        if (sku) map[sku] = product;
        return map;
      },
      {}
    );

    // Map Criteo products to Shopify products (preserving duplicates from Criteo)
    /**
     * @type {(import("./_criteoApi").IShopiProduct & { criteo: object })[]}
     */
    const shopifyProducts = criteoProducts
      .map((criteoProduct) => {
        const shopifyProduct = shopifyProductsMap[criteoProduct.ProductId];
        if (shopifyProduct) {
          return { ...shopifyProduct, criteo: criteoProduct };
        }
        return null;
      })
      .filter(Boolean);

    // Create product cards
    const productsZone = showcase.querySelector(this.#selectors.productsZone);
    const productCards = shopifyProducts.map((product) =>
      this.#createProductCard(product)
    );

    productCards.forEach((card) => productsZone.appendChild(card));

    // Setup slider arrow navigation
    this.#setupSliderArrow(showcase, productsZone);

    // Add sponsored badge
    this._sponsoredBadge(showcaseContainer);

    // Set placement-level onView beacon for the showcase container
    // Only criteoPlacementViewBeacon is needed here — criteoViewBeacon is for
    // individual product cards (set in #createProductCard). Having both on the
    // same element with the same URL caused the placement onView to fire twice.
    showcaseContainer.dataset.criteoPlacementViewBeacon = OnViewBeacon;

    this.#log("=== SETTING UP BEACONS ===");
    this.#log("OnViewBeacon:", OnViewBeacon);
    this.#log("OnLoadBeacon:", OnLoadBeacon);
    this.#log("OnClickBeacon:", OnClickBeacon);
    this.#log("Product beacons:", shopifyProducts.map(p => ({
      sku: p.tags.find(t => t.startsWith('sku-')),
      onView: p.criteo?.OnViewBeacon,
      onClick: p.criteo?.OnClickBeacon,
      onLoad: p.criteo?.OnLoadBeacon
    })));

    // Prepare onClick elements
    const clickableElements = [
      ...productCards.map((card) =>
        card.querySelector(this.#selectors.productLink)
      ),
    ];

    // Add brandCta if it has a click URL (works for both creative and rendering formats)
    const brandCtaElement = showcase.querySelector(this.#selectors.brandCta);
    if (brandCtaElement && brandCtaElement.dataset.criteoOnClickBeacon) {
      this.#log("Adding brandCta to clickable elements");
      clickableElements.push(brandCtaElement);
    }

    this.#log("Setting up click events for", clickableElements.length, "elements");
    this._onClickEvent(clickableElements);

    const loadBeacons = [
      OnLoadBeacon,
      ...shopifyProducts.map((p) => p.criteo?.OnLoadBeacon),
    ].filter(Boolean);

    this.#log("Setting up load events for", loadBeacons.length, "beacons");
    this._onLoadEvent(loadBeacons);

    this.#showcaseElement = showcaseContainer;

    // Store elements for viewport tracking (will be set up after DOM insertion)
    showcaseContainer._viewportElements = [showcaseContainer, ...productCards];

    return showcaseContainer;
  }

  /**
   * Execute Showcase rendering for collection pages
   * @param {'search-results-page' | 'collections'} template
   */
  async exec(template) {
    try {
      if (!CriteoShowcase.isEnabled) return;

      if (!this._isActive) return;

      // Check if showcase was removed from DOM (e.g., by Searchanise re-render on viewport change)
      if (this.#hasRendered && this.#showcaseElement) {
        const stillInDOM = document.contains(this.#showcaseElement);
        if (!stillInDOM) {
          this.#log("Showcase was removed from DOM, allowing re-render");
          this.#hasRendered = false;
          this.#showcaseElement = null;
        }
      }

      if (this.#hasRendered) return;

      const grid = await this.#grid();
      const params = await this.#generateParams(template);

      const eventType = template === "collections" ? "viewCategory" : "viewSearchResult";

      this.#log("=== CRITEO API REQUEST ===");
      this.#log("Event Type:", eventType);
      this.#log("Request Params:", params);
      this.#log("Base URL:", window.mmkt?.config?.criteoAPI?.baseURL);
      this.#log("Partner ID:", window.mmkt?.config?.criteoAPI?.partnerId);

      if (!this.#response) {
        this.#response = await this._baseRequest(eventType, params);
      }

      this.#log("=== CRITEO API RESPONSE ===");
      this.#log("Full API Response:", this.#response);

      const { placements } = this.#response;

      if (!placements.length) {
        this.#log("No placements available");
        this.#log("Response structure:", this.#response);
        return;
      }

      this.#log("Placements found:", placements.length);
      this.#log("Placements data:", placements);

      const device = screen.width <= 750 ? "Mobile" : "Desktop";
      const deviceLower = device.toLowerCase();
      const viewType = template === "collections" ? "Category" : "SearchResult";

      // Look for Showcase placement in response
      // Try multiple naming conventions based on Criteo documentation
      const showcaseKeys = [
        // Format 1: viewSearchResultApiDesktop-Showcase (current format)
        `view${viewType}Api${device}-Showcase`,
        `view${viewType}Api${device}-Showcase01`,

        // Format 2: viewSearchResult_API_desktop-Showcase (documented format)
        `view${viewType}_API_${deviceLower}-Showcase`,
        `view${viewType}_API_${deviceLower}-Showcase01`,

        // Format 3: Other possible variations
        `view${viewType}_API_${device}-Showcase`,
        `view${viewType}Api${deviceLower}-Showcase`,

        // Format 4: topBranding placement (alternative Showcase format)
        `view${viewType}Api${device}-topBranding`,
        `view${viewType}Api${deviceLower}-topBranding`,
      ];

      this.#log("Looking for Showcase placement keys:", showcaseKeys);
      this.#log("Available placement keys:", placements.map(p => Object.keys(p)));

      const showcasePlacement = placements.find(
        (placement) => showcaseKeys.some(key => placement[key])
      );

      if (!showcasePlacement) {
        this.#log("No Showcase placement found in response");
        this.#log("Searched for keys:", showcaseKeys);
        return;
      }

      this.#log("Found Showcase placement:", showcasePlacement);

      const showcaseKey =
        Object.keys(showcasePlacement).find(
          (key) => (key.includes("Showcase") || key.includes("topBranding")) && Array.isArray(showcasePlacement[key])
        );

      this.#log("Showcase key identified:", showcaseKey);

      if (!showcaseKey || !showcasePlacement[showcaseKey]?.length) {
        this.#log("Invalid Showcase data - no valid key or empty array");
        return;
      }

      const [showcaseData] = showcasePlacement[showcaseKey];
      this.#log("Showcase data extracted:", showcaseData);

      // Render the Showcase
      const showcaseElement = await this.#renderShowcase(showcaseData);

      // Insert Showcase at the top of the grid (first position)
      if (grid.firstChild) {
        grid.insertBefore(showcaseElement, grid.firstChild);
      } else {
        grid.appendChild(showcaseElement);
      }

      // IntersectionObserver instead of parent's _viewPortEvent: #isInViewport
      // requires full element visibility (impossible on mobile), and document.scroll
      // misses horizontal scroll inside the products container.
      requestAnimationFrame(() => {
        const viewportElements = showcaseElement._viewportElements;
        if (viewportElements) {
          this.#log("Setting up IntersectionObserver for showcase + products (after DOM insertion)");
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !entry.target.getAttribute("viewport-visible")) {
                entry.target.setAttribute("viewport-visible", "true");
                const beacon = entry.target.dataset.criteoViewBeacon;
                const placementBeacon = entry.target.dataset.criteoPlacementViewBeacon;
                if (placementBeacon) {
                  this.#log("[OnView] Sending placement beacon via IntersectionObserver");
                  this.#sendViewBeacon(placementBeacon);
                }
                if (beacon) {
                  this.#log("[OnView] Sending view beacon via IntersectionObserver");
                  this.#sendViewBeacon(beacon);
                }
                observer.unobserve(entry.target);
              }
            });
          }, { threshold: 0.5 });
          viewportElements.forEach(el => observer.observe(el));
          delete showcaseElement._viewportElements;
        }
      });

      this.#hasRendered = true;
      this.#log("Showcase rendered successfully");
    } catch (error) {
      this.#log("Error rendering Showcase:", error);
    }
  }

  #sendViewBeacon(url) {
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url);
      } else {
        fetch(url, { method: "GET", keepalive: true });
      }
    } catch (error) {
      this.#log("Failed to send view beacon:", error);
    }
  }

  reset() {
    this.#response = null;
    this.#hasRendered = false;
    this.#showcaseElement = null;
  }
}
